import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  DocumentMagnifyingGlassIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  useExequaturVerification,
  useVerificationRequestDetail,
} from "../hooks/useExequaturVerification";
import type { AdminLawyerExequaturRow } from "@/shared/db";
import type { ExequaturDisplayStatus } from "../hooks/useExequaturVerification";
import { Input, Select, Button } from "@/shared/ui";
import { ConfirmModal } from "@/shared/ui";

type ActionType = "approve" | "reject" | "invalid" | "review" | null;

export const ExequaturVerificationPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"users" | "requests">("users");
  const [filters, setFilters] = useState<{
    exequatur_status?: ExequaturDisplayStatus;
    country?: string;
    search?: string;
  }>({});
  const [detailLawyer, setDetailLawyer] =
    useState<AdminLawyerExequaturRow | null>(null);
  const [actionModal, setActionModal] = useState<{
    type: ActionType;
    lawyer: AdminLawyerExequaturRow;
    requestId: string | null;
  } | null>(null);
  const [reasonSelect, setReasonSelect] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [notifyingMissing, setNotifyingMissing] = useState(false);

  const REASON_KEYS = [
    "invalid_format",
    "duplicate",
    "unverifiable",
    "expired",
    "wrong_country",
    "doc_illegible",
    "other",
  ] as const;

  const getReasonForUser = () => {
    if (reasonSelect === "other") return reasonInput.trim();
    if (reasonSelect) return t(`admin.exequatur.reasons.${reasonSelect}`);
    return reasonInput.trim();
  };

  const {
    lawyers,
    requests,
    loading,
    error,
    refetch,
    approveRequest,
    rejectRequest,
    markInvalid,
    sendToReview,
    approveOrRejectDirect,
    notifyMissingExequatur,
  } = useExequaturVerification(activeTab, filters);
  const { history: detailHistory, loading: detailLoading } =
    useVerificationRequestDetail(detailLawyer?.id ?? null);

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === "all" ? undefined : value,
    }));
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "verificado":
        return "bg-green-100 text-green-800";
      case "pendiente":
      case "en_revision":
        return "bg-amber-100 text-amber-800";
      case "invalido":
      case "rechazado":
        return "bg-red-100 text-red-800";
      case "no_enviado":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleApprove = async () => {
    if (!actionModal) return;
    const ok = actionModal.requestId
      ? await approveRequest(actionModal.requestId, actionModal.lawyer.id)
      : await approveOrRejectDirect(actionModal.lawyer.id, "approved");
    if (ok) {
      setActionModal(null);
      setDetailLawyer(null);
    }
  };

  const handleReject = async () => {
    if (!actionModal) return;
    const reason = getReasonForUser();
    if (!reason) return;
    const ok = actionModal.requestId
      ? await rejectRequest(actionModal.requestId, actionModal.lawyer.id, reason)
      : await approveOrRejectDirect(actionModal.lawyer.id, "rejected", reason);
    if (ok) {
      setActionModal(null);
      setReasonSelect("");
      setReasonInput("");
      setDetailLawyer(null);
    }
  };

  const handleMarkInvalid = async () => {
    if (!actionModal) return;
    const reason = getReasonForUser();
    if (!reason) return;
    const ok = actionModal.requestId
      ? await markInvalid(actionModal.requestId, actionModal.lawyer.id, reason)
      : await approveOrRejectDirect(actionModal.lawyer.id, "rejected", `inválido: ${reason}`);
    if (ok) {
      setActionModal(null);
      setReasonSelect("");
      setReasonInput("");
      setDetailLawyer(null);
    }
  };

  const handleSendToReview = async () => {
    if (!actionModal || !actionModal.requestId) return;
    const ok = await sendToReview(actionModal.requestId, actionModal.lawyer.id);
    if (ok) {
      setActionModal(null);
      setDetailLawyer(null);
    }
  };

  const handleNotifyMissing = async () => {
    const withoutExequatur = lawyers
      .filter((l) => l.exequatur_display_status === "no_enviado")
      .map((l) => l.id);
    if (withoutExequatur.length === 0) {
      toast.info(t("admin.exequatur.noMissingToNotify"));
      return;
    }
    setNotifyingMissing(true);
    try {
      const sent = await notifyMissingExequatur(withoutExequatur);
      toast.success(t("admin.exequatur.notifiedMissing", { count: sent }));
      refetch();
    } finally {
      setNotifyingMissing(false);
    }
  };

  const openActionModal = (
    type: ActionType,
    lawyer: AdminLawyerExequaturRow,
    requestId: string | null,
  ) => {
    setActionModal({ type, lawyer, requestId });
    setReasonSelect("");
    setReasonInput("");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-juridico">
          {t("admin.exequatur.title")}
        </h1>
        <p className="text-juridico/60 mt-1">{t("admin.exequatur.subtitle")}</p>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label={t("admin.exequatur.tabsLabel")} className="flex gap-2 border-b border-romance-200">
        <button
          onClick={() => setActiveTab("users")}
          role="tab"
          aria-selected={activeTab === "users"}
          aria-controls="exequatur-users-panel"
          id="exequatur-users-tab"
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 ${
            activeTab === "users"
              ? "bg-tiber-500 text-white"
              : "text-juridico hover:bg-romance-50"
          }`}
        >
          <span className="flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5" />
            {t("admin.exequatur.tabs.users")}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("requests")}
          role="tab"
          aria-selected={activeTab === "requests"}
          aria-controls="exequatur-requests-panel"
          id="exequatur-requests-tab"
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 ${
            activeTab === "requests"
              ? "bg-tiber-500 text-white"
              : "text-juridico hover:bg-romance-50"
          }`}
        >
          <span className="flex items-center gap-2">
            <DocumentMagnifyingGlassIcon className="w-5 h-5" />
            {t("admin.exequatur.tabs.requests")}
          </span>
        </button>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <form onSubmit={(e) => e.preventDefault()} className="lg:col-span-2">
            <Input
              type="text"
              value={filters.search ?? ""}
              onChange={(e) =>
                handleFilterChange("search", e.target.value || undefined)
              }
              placeholder={t("admin.exequatur.filters.search")}
              leftIcon={
                <MagnifyingGlassIcon className="w-5 h-5 text-tiber-900/40" />
              }
            />
          </form>
          <Select
            value={filters.exequatur_status ?? "all"}
            onChange={(e) =>
              handleFilterChange(
                "exequatur_status",
                e.target.value as ExequaturDisplayStatus | "all",
              )
            }
            options={[
              { value: "all", label: t("admin.exequatur.filters.allStatus") },
              {
                value: "no_enviado",
                label: t("admin.exequatur.status.no_enviado"),
              },
              {
                value: "pendiente",
                label: t("admin.exequatur.status.pendiente"),
              },
              {
                value: "verificado",
                label: t("admin.exequatur.status.verificado"),
              },
              {
                value: "invalido",
                label: t("admin.exequatur.status.invalido"),
              },
              {
                value: "rechazado",
                label: t("admin.exequatur.status.rechazado"),
              },
              {
                value: "en_revision",
                label: t("admin.exequatur.status.en_revision"),
              },
            ]}
          />
          <Select
            value={filters.country ?? "all"}
            onChange={(e) => handleFilterChange("country", e.target.value)}
            options={[
              { value: "all", label: t("common.allCountries") },
              { value: "RD", label: t("common.countries.RD") },
              { value: "MX", label: t("common.countries.MX") },
              { value: "CO", label: t("common.countries.CO") },
            ]}
          />
        </div>
        <div className="mt-4 flex justify-between items-center flex-wrap gap-2">
          <p className="text-sm text-juridico/60">
            {activeTab === "users"
              ? t("admin.exequatur.showing", { count: lawyers.length })
              : t("admin.exequatur.showingRequests", {
                  count: requests.length,
                })}
          </p>
          <div className="flex items-center gap-2">
            {activeTab === "users" &&
              lawyers.some((l) => l.exequatur_display_status === "no_enviado") && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleNotifyMissing}
                  disabled={notifyingMissing}
                >
                  {notifyingMissing ? t("common.loading") : t("admin.exequatur.actions.notifyMissing")}
                </Button>
              )}
            <button
              onClick={refetch}
              className="text-sm text-tiber-500 hover:text-tiber-500/80 font-medium"
            >
              {t("common.refresh")}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-lg border border-romance-200 overflow-hidden"
      >
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-500" />
            <p className="text-red-600">{error}</p>
          </div>
        ) : activeTab === "users" ? (
          <div id="exequatur-users-panel" role="tabpanel" aria-labelledby="exequatur-users-tab" className="overflow-x-auto">
            <table className="min-w-full divide-y divide-romance-200">
              <thead className="bg-romance-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.user")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.exequatur")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.lastReview")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-romance-200">
                {lawyers.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-romance-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-juridico">
                          {row.full_name || t("common.notAvailable")}
                        </div>
                        <div className="text-sm text-juridico/60">
                          {row.email}
                        </div>
                        <div className="text-xs text-juridico/50">
                          {row.country}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-juridico">
                        {row.bar_number || t("admin.exequatur.noExequatur")}
                      </div>
                      {row.university && (
                        <div className="text-xs text-juridico/60">
                          {row.university}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${getStatusBadgeClass(
                          row.exequatur_display_status,
                        )}`}
                      >
                        {t(
                          `admin.exequatur.status.${row.exequatur_display_status}`,
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-juridico/60">
                      {row.last_reviewed_at
                        ? new Date(row.last_reviewed_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(row.exequatur_display_status === "no_enviado" ||
                          row.exequatur_display_status === "pendiente" ||
                          row.exequatur_display_status === "en_revision") && (
                          <>
                            <button
                              onClick={() =>
                                openActionModal("approve", row, row.latest_verification_request_id ?? null)
                              }
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500"
                              aria-label={t("admin.exequatur.actions.approve")}
                              title={t("admin.exequatur.actions.approve")}
                            >
                              <CheckCircleIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() =>
                                openActionModal("reject", row, row.latest_verification_request_id ?? null)
                              }
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500"
                              aria-label={t("admin.exequatur.actions.reject")}
                              title={t("admin.exequatur.actions.reject")}
                            >
                              <XCircleIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setDetailLawyer(row)}
                          className="text-tiber-500 hover:text-tiber-600 font-medium text-sm p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2"
                          aria-label={t("admin.exequatur.actions.viewDetail")}
                          type="button"
                        >
                          <EyeIcon className="w-5 h-5 inline" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div id="exequatur-requests-panel" role="tabpanel" aria-labelledby="exequatur-requests-tab" className="overflow-x-auto">
            <table className="min-w-full divide-y divide-romance-200">
              <thead className="bg-romance-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.user")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.exequatur")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.created")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.exequatur.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-romance-200">
                {requests.map((req) => {
                  const profiles = req.lawyer_profiles?.profiles;
                  const lawyerProfile = req.lawyer_profiles;
                  const name =
                    profiles?.full_name ||
                    req.email ||
                    t("common.notAvailable");
                  const email = profiles?.email;
                  const lawyerRow: AdminLawyerExequaturRow = {
                    id: req.lawyer_id,
                    email: email ?? null,
                    full_name: name,
                    country: profiles?.country ?? null,
                    profile_created_at: null,
                    bar_number: lawyerProfile?.bar_number ?? req.bar_number,
                    university: lawyerProfile?.university ?? req.university,
                    is_verified: false,
                    verification_date: null,
                    lawyer_profile_created_at: null,
                    latest_verification_request_id: req.id,
                    verification_status: req.status,
                    verification_method: req.verification_method,
                    last_reviewed_at: req.reviewed_at,
                    reviewed_by: req.reviewed_by,
                    rejection_reason: req.rejection_reason,
                    verification_notes: req.notes,
                    auto_verified: req.auto_verified,
                    auto_verification_score: req.auto_verification_score,
                    exequatur_display_status:
                      req.status === "approved"
                        ? "verificado"
                        : req.status === "rejected"
                          ? "rechazado"
                          : req.status === "in_review"
                            ? "en_revision"
                            : "pendiente",
                  };
                  return (
                    <tr
                      key={req.id}
                      className="hover:bg-romance-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-juridico">
                            {name}
                          </div>
                          <div className="text-sm text-juridico/60">
                            {email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-juridico">
                        {req.bar_number || t("admin.exequatur.noExequatur")}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${getStatusBadgeClass(
                            lawyerRow.exequatur_display_status,
                          )}`}
                        >
                          {t(
                            `admin.exequatur.status.${lawyerRow.exequatur_display_status}`,
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-juridico/60">
                        {req.created_at
                          ? new Date(req.created_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => setDetailLawyer(lawyerRow)}
                          className="text-tiber-500 hover:text-tiber-600 font-medium text-sm p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2"
                          aria-label={t("admin.exequatur.actions.viewDetail")}
                          type="button"
                        >
                          <EyeIcon className="w-5 h-5 inline" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {(activeTab === "users"
          ? lawyers.length === 0
          : requests.length === 0) &&
          !loading && (
            <div className="flex justify-center py-12">
              <p className="text-juridico/60">
                {t("admin.exequatur.noResults")}
              </p>
            </div>
          )}
      </motion.div>

      {/* Detail Modal */}
      {detailLawyer && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDetailLawyer(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="exequatur-detail-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2
                id="exequatur-detail-title"
                className="text-xl font-display font-bold text-juridico mb-4"
              >
                {t("admin.exequatur.detail.title")}
              </h2>
              <div className="space-y-3 text-sm">
                <p>
                  <span className="font-medium text-juridico">
                    {t("admin.exequatur.detail.name")}:
                  </span>{" "}
                  {detailLawyer.full_name || t("common.notAvailable")}
                </p>
                <p>
                  <span className="font-medium text-juridico">
                    {t("admin.exequatur.detail.email")}:
                  </span>{" "}
                  {detailLawyer.email}
                </p>
                <p>
                  <span className="font-medium text-juridico">
                    {t("admin.exequatur.detail.barNumber")}:
                  </span>{" "}
                  {detailLawyer.bar_number || t("admin.exequatur.noExequatur")}
                </p>
                <p>
                  <span className="font-medium text-juridico">
                    {t("admin.exequatur.detail.university")}:
                  </span>{" "}
                  {detailLawyer.university || "—"}
                </p>
                <p>
                  <span className="font-medium text-juridico">
                    {t("admin.exequatur.detail.country")}:
                  </span>{" "}
                  {detailLawyer.country || "—"}
                </p>
                <p>
                  <span className="font-medium text-juridico">
                    {t("admin.exequatur.detail.verificationStatus")}:
                  </span>{" "}
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                      detailLawyer.exequatur_display_status,
                    )}`}
                  >
                    {t(
                      `admin.exequatur.status.${detailLawyer.exequatur_display_status}`,
                    )}
                  </span>
                </p>
                {detailLawyer.auto_verification_score != null && (
                  <p>
                    <span className="font-medium text-juridico">
                      {t("admin.exequatur.detail.score")}:
                    </span>{" "}
                    {detailLawyer.auto_verification_score}
                  </p>
                )}
                {detailLawyer.rejection_reason && (
                  <p>
                    <span className="font-medium text-juridico">
                      {t("admin.exequatur.detail.rejectionReason")}:
                    </span>{" "}
                    {detailLawyer.rejection_reason}
                  </p>
                )}
              </div>

              {detailLoading ? (
                <div className="mt-4 animate-spin h-6 w-6 border-2 border-tiber-500 rounded-full border-t-transparent" />
              ) : (
                detailHistory.length > 0 && (
                  <div className="mt-4">
                    <h3 className="font-medium text-juridico mb-2">
                      {t("admin.exequatur.detail.history")}
                    </h3>
                    <ul className="space-y-1 text-sm text-juridico/80">
                      {detailHistory.slice(0, 5).map((h) => (
                        <li key={h.id}>
                          {h.status} —{" "}
                          {h.reviewed_at
                            ? new Date(h.reviewed_at).toLocaleDateString()
                            : new Date(h.created_at).toLocaleDateString()}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )}

              {(detailLawyer.latest_verification_request_id ||
                detailLawyer.exequatur_display_status === "no_enviado") &&
                detailLawyer.verification_status !== "approved" && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {(detailLawyer.verification_status === "pending" ||
                      detailLawyer.exequatur_display_status === "no_enviado") && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            openActionModal(
                              "approve",
                              detailLawyer,
                              detailLawyer.latest_verification_request_id ?? null,
                            )
                          }
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1 inline" />
                          {t("admin.exequatur.actions.approve")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            openActionModal(
                              "reject",
                              detailLawyer,
                              detailLawyer.latest_verification_request_id ?? null,
                            )
                          }
                        >
                          <XCircleIcon className="w-4 h-4 mr-1 inline" />
                          {t("admin.exequatur.actions.reject")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            openActionModal(
                              "invalid",
                              detailLawyer,
                              detailLawyer.latest_verification_request_id ?? null,
                            )
                          }
                        >
                          {t("admin.exequatur.actions.markInvalid")}
                        </Button>
                        {detailLawyer.latest_verification_request_id && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              openActionModal(
                                "review",
                                detailLawyer,
                                detailLawyer.latest_verification_request_id!,
                              )
                            }
                          >
                            {t("admin.exequatur.actions.sendToReview")}
                          </Button>
                        )}
                      </>
                    )}
                    {detailLawyer.verification_status === "in_review" && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            openActionModal(
                              "approve",
                              detailLawyer,
                              detailLawyer.latest_verification_request_id ?? null,
                            )
                          }
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1 inline" />
                          {t("admin.exequatur.actions.approve")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            openActionModal(
                              "reject",
                              detailLawyer,
                              detailLawyer.latest_verification_request_id ?? null,
                            )
                          }
                        >
                          <XCircleIcon className="w-4 h-4 mr-1 inline" />
                          {t("admin.exequatur.actions.reject")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            openActionModal(
                              "invalid",
                              detailLawyer,
                              detailLawyer.latest_verification_request_id ?? null,
                            )
                          }
                        >
                          {t("admin.exequatur.actions.markInvalid")}
                        </Button>
                      </>
                    )}
                  </div>
                )}

              <div className="mt-6 flex justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setDetailLawyer(null)}
                >
                  {t("common.close")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (approve / reject / invalid) */}
      {actionModal && actionModal.type === "approve" && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setActionModal(null)}
          onConfirm={handleApprove}
          title={t("admin.exequatur.confirm.approveTitle")}
          message={t("admin.exequatur.confirm.approveMessage")}
          confirmText={t("admin.exequatur.actions.approve")}
          cancelText={t("common.cancel")}
        />
      )}
      {actionModal && actionModal.type === "reject" && (
        <div
          className="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4"
          onClick={() => setActionModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-juridico mb-2">
              {t("admin.exequatur.confirm.rejectTitle")}
            </h3>
            <p className="text-sm text-juridico/70 mb-4">
              {t("admin.exequatur.confirm.rejectMessage")}
            </p>
            <Select
              value={reasonSelect}
              onChange={(e) => setReasonSelect(e.target.value)}
              options={[
                { value: "", label: t("admin.exequatur.confirm.reasonPlaceholder") },
                ...REASON_KEYS.map((k) => ({
                  value: k,
                  label: t(`admin.exequatur.reasons.${k}`),
                })),
              ]}
            />
            {reasonSelect === "other" && (
              <textarea
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                placeholder={t("admin.exequatur.confirm.reasonPlaceholder")}
                className="w-full mt-2 border border-romance-200 rounded-lg p-3 text-sm min-h-[80px]"
              />
            )}
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="secondary" onClick={() => setActionModal(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                onClick={handleReject}
                disabled={!(reasonSelect ? (reasonSelect === "other" ? reasonInput.trim() : true) : reasonInput.trim())}
              >
                {t("admin.exequatur.actions.reject")}
              </Button>
            </div>
          </div>
        </div>
      )}
      {actionModal && actionModal.type === "invalid" && (
        <div
          className="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4"
          onClick={() => setActionModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-juridico mb-2">
              {t("admin.exequatur.confirm.invalidTitle")}
            </h3>
            <p className="text-sm text-juridico/70 mb-4">
              {t("admin.exequatur.confirm.invalidMessage")}
            </p>
            <Select
              value={reasonSelect}
              onChange={(e) => setReasonSelect(e.target.value)}
              options={[
                { value: "", label: t("admin.exequatur.confirm.reasonPlaceholder") },
                ...REASON_KEYS.map((k) => ({
                  value: k,
                  label: t(`admin.exequatur.reasons.${k}`),
                })),
              ]}
            />
            {reasonSelect === "other" && (
              <textarea
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                placeholder={t("admin.exequatur.confirm.reasonPlaceholder")}
                className="w-full mt-2 border border-romance-200 rounded-lg p-3 text-sm min-h-[80px]"
              />
            )}
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="secondary" onClick={() => setActionModal(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                onClick={handleMarkInvalid}
                disabled={!(reasonSelect ? (reasonSelect === "other" ? reasonInput.trim() : true) : reasonInput.trim())}
              >
                {t("admin.exequatur.actions.markInvalid")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {actionModal && actionModal.type === "review" && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setActionModal(null)}
          onConfirm={handleSendToReview}
          title={t("admin.exequatur.confirm.reviewTitle")}
          message={t("admin.exequatur.confirm.reviewMessage")}
          confirmText={t("admin.exequatur.actions.sendToReview")}
          cancelText={t("common.cancel")}
        />
      )}
    </div>
  );
};
